// Client API for the staff-only Enquiries tracker (Phase 1). Online-only (creating/editing/
// logging changes server authority); the page gates on the connection state.

import { apiFetch } from './api';

export type EnquirySource = 'phone_service' | 'website_form' | 'manual' | 'email';
export type EnquiryStatus = 'new' | 'in_progress' | 'awaiting_followup' | 'resolved' | 'closed';
export type EnquiryPriority = 'normal' | 'urgent';
export type EnquiryActionType =
  | 'note' | 'contact_attempt' | 'contact_made' | 'outcome' | 'status_change' | 'assignment_change' | 'escalation'
  | 'followup_reminder';

export interface PersonRef { id: string; full_name: string; preferred_name: string | null; pilot_code?: string | null; first_name?: string | null; last_name?: string | null; }
export interface BaseRef { id: string; name: string; short_code: string; }

export interface EnquiryAssignee {
  id: string;
  user_id: string;
  assigned_at: string;
  user: PersonRef;
}

// Resolved actor for a timeline entry (null = system-generated, e.g. escalations).
export interface ActionActor {
  id: string;
  pilot_code: string | null;
  full_name: string;
  first_name: string | null;
  last_name: string | null;
}

export interface EnquiryAction {
  id: string;
  actor_id: string | null;
  actor: ActionActor | null;
  action_type: EnquiryActionType;
  outcome: string | null;
  follow_up_at: string | null;
  body: string | null;
  created_at: string;
}

export interface EnquiryListItem {
  id: string;
  source: EnquirySource;
  status: EnquiryStatus;
  priority: EnquiryPriority;
  enquirer_name: string | null;
  enquirer_phone: string | null;
  enquirer_email: string | null;
  enquirer_company: string | null;
  subject: string | null;
  body: string;
  base_id: string | null;
  base: BaseRef | null;
  received_at: string;
  next_followup_at: string | null;
  resolved_at: string | null;
  escalation_level: number;
  created_at: string;
  assignees: { user_id: string; user: PersonRef }[];
  _count?: { actions: number };
  overdue: boolean;
  due_for_followup: boolean;
  // A 1300 call that arrived with a location the server could not map to a base (PR-000053).
  // Derived server-side, not stored; goes false once a base is assigned.
  needs_base?: boolean;
}

export interface EnquiryDetail extends EnquiryListItem {
  assignees: EnquiryAssignee[];
  actions: EnquiryAction[];
  // The location the caller gave, for the triage hint beside the Base picker. Null once a base
  // is set, or when the enquiry is not a base-less 1300 call.
  caller_location?: string | null;
}

// Multi-select facets: any-of within a facet, and-across facets. Empty array = no filter on that
// facet. Values are comma-joined per facet. `assignees` accepts the 'me' shortcut; `baseIds`
// accepts 'none' for the Unassigned (no-base) option.
export interface ListParams {
  statuses?: EnquiryStatus[];
  assignees?: string[];   // user ids and/or 'me'
  baseIds?: string[];     // base ids and/or 'none'
  q?: string;             // keyword search
  creators?: string[];    // creator/assignor user ids and/or 'me'
  created_from?: string;  // YYYY-MM-DD inclusive
  created_to?: string;    // YYYY-MM-DD inclusive
  tz?: string;            // IANA device timezone; sent only alongside a date filter (T2)
  overdue?: boolean;
  due_for_followup?: boolean;
}

export async function listEnquiries(p: ListParams = {}): Promise<EnquiryListItem[]> {
  const q = new URLSearchParams();
  if (p.statuses?.length) q.set('status', p.statuses.join(','));
  if (p.assignees?.length) q.set('assignee', p.assignees.join(','));
  if (p.baseIds?.length) q.set('base_id', p.baseIds.join(','));
  if (p.q?.trim()) q.set('q', p.q.trim());
  if (p.creators?.length) q.set('creator', p.creators.join(','));
  if (p.created_from) q.set('created_from', p.created_from);
  if (p.created_to) q.set('created_to', p.created_to);
  // Only meaningful with a date bound — send the device tz so the day maps in the user's zone.
  if ((p.created_from || p.created_to) && p.tz) q.set('tz', p.tz);
  if (p.overdue) q.set('overdue', 'true');
  if (p.due_for_followup) q.set('due_for_followup', 'true');
  const res = await apiFetch<EnquiryListItem[]>(`/api/enquiries${q.toString() ? `?${q}` : ''}`);
  return res.error || !res.data ? [] : res.data;
}

export async function getEnquiry(id: string): Promise<EnquiryDetail | null> {
  const res = await apiFetch<EnquiryDetail>(`/api/enquiries/${id}`);
  return res.error ? null : res.data;
}

// Homepage alert badge — counts only (open / assigned-to-me / overdue).
export interface EnquirySummary {
  open_total: number;
  assigned_to_me_open: number;
  overdue_total: number;
  overdue_mine: number;
}
export async function fetchSummary(): Promise<EnquirySummary | null> {
  const res = await apiFetch<EnquirySummary>('/api/enquiries/summary');
  return res.error || !res.data ? null : res.data;
}

export interface CreateInput {
  source?: EnquirySource;
  priority?: EnquiryPriority;
  enquirer_name?: string | null;
  enquirer_phone?: string | null;
  enquirer_email?: string | null;
  enquirer_company?: string | null;
  subject?: string | null;
  body: string;
  base_id?: string | null;
}

export async function createEnquiry(input: CreateInput): Promise<{ ok: boolean; id?: string; error?: string }> {
  const res = await apiFetch<{ id: string }>('/api/enquiries', { method: 'POST', body: JSON.stringify(input) });
  return res.error ? { ok: false, error: res.error.message } : { ok: true, id: res.data?.id };
}

export async function patchEnquiry(id: string, patch: Partial<CreateInput> & { status?: EnquiryStatus; priority?: EnquiryPriority }): Promise<{ ok: boolean; error?: string }> {
  const res = await apiFetch(`/api/enquiries/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
  return res.error ? { ok: false, error: res.error.message } : { ok: true };
}

export async function addAssignee(id: string, userId: string): Promise<{ ok: boolean; error?: string }> {
  const res = await apiFetch(`/api/enquiries/${id}/assignees`, { method: 'POST', body: JSON.stringify({ user_id: userId }) });
  return res.error ? { ok: false, error: res.error.message } : { ok: true };
}

export async function removeAssignee(id: string, userId: string): Promise<{ ok: boolean; error?: string }> {
  const res = await apiFetch(`/api/enquiries/${id}/assignees/${userId}`, { method: 'DELETE' });
  return res.error ? { ok: false, error: res.error.message } : { ok: true };
}

export interface ActionInput {
  action_type: 'note' | 'contact_attempt' | 'contact_made' | 'outcome';
  outcome: string;
  follow_up_at?: string;  // ISO with offset; omitted → the server auto-defers by its default interval
  resolved?: boolean;
  reminder_enabled?: boolean; // PR-000045: arm a reminder for the (chosen or auto) follow-up
  body?: string | null;
}

export async function logAction(id: string, input: ActionInput): Promise<{ ok: boolean; error?: string }> {
  const res = await apiFetch(`/api/enquiries/${id}/actions`, { method: 'POST', body: JSON.stringify(input) });
  return res.error ? { ok: false, error: res.error.message } : { ok: true };
}

// Staff + bases for the pickers (reuse existing endpoints).
export interface StaffOption {
  id: string;
  full_name: string;
  first_name: string | null;
  last_name: string | null;
  preferred_name: string | null;
  pilot_code: string | null;
  home_base_id: string | null;
}
export async function fetchStaff(): Promise<StaffOption[]> {
  const res = await apiFetch<StaffOption[]>('/api/staff');
  return res.error || !res.data ? [] : res.data;
}
export async function fetchBases(): Promise<BaseRef[]> {
  const res = await apiFetch<BaseRef[]>('/api/bases');
  return res.error || !res.data ? [] : res.data;
}
